/**
 * ISO 4217 现行币种码全集（SIX ISO 4217 Maintenance Agency List One：current currencies & funds）与「可定价币种」判定。
 *
 * 用途：框架通道价格币种的写入校验（`payment_channel_prices.currency`，A8 §10.3）与 admin 选价镜像（A8 §1
 * `CHANNEL_PRICE_UNSELECTABLE`）、web 币种下拉，server 入口与 web 共用这一份。`^[A-Z]{3}$` 只是形状，`XYZ` 这类
 * 不存在的码会被当成可售价格写进 C 端读取的表。
 *
 * 可定价币种（字典 65 §9，dev-docs#1532）= List One 现行码中小数位有定义的码：框架通道金额以 minor units 存储、
 * 按该币种 ISO 4217 小数位换算主单位（框架 §7.2），List One 中小数位为 N.A. 的码（贵金属、债券市场记账单位、SDR
 * 等记账单位、测试码与无货币码，`ISO_4217_NO_MINOR_UNIT_CODES`）没有 minor units 定义，不是可定价币种。
 * `isPriceableCurrency` 与 C 端 `novel-app-server` `src/lib/iso4217-minor-units.ts` 的同名判定同口径；现行码表与
 * C 端 `ISO_4217_CURRENT_CODES`（List One 2026-09-17 版）逐码同表，改动须两边一起改（小数位另见
 * `admob-revenue-money.ts` 与 web `iso4217-minor-units.ts`）。
 * 来源：https://www.six-group.com/dam/download/financial-information/data-center/iso-currrency/lists/list-one.xml
 */

/** List One 现行币种码（字母序）。 */
export const ISO_4217_CURRENT_CURRENCY_CODES: readonly string[] = Object.freeze(
  'AED AFN ALL AMD AOA ARS AUD AWG AZN BAM BBD BDT BHD BIF BMD BND BOB BOV BRL BSD BTN BWP BYN BZD CAD CDF CHE CHF CHW CLF CLP CNY COP COU CRC CUP CVE CZK DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD GNF GTQ GYD HKD HNL HTG HUF IDR ILS INR IQD IRR ISK JMD JOD JPY KES KGS KHR KMF KPW KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MXV MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK SGD SHP SLE SOS SRD SSP STN SVC SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD USN UYI UYU UYW UZS VED VES VND VUV WST XAD XAF XAG XAU XBA XBB XBC XBD XCD XCG XDR XOF XPD XPF XPT XSU XTS XUA XXX YER ZAR ZMW ZWG'.split(
    ' ',
  ),
);

/**
 * List One 中小数位为 N.A. 的现行码（贵金属 / 记账单位 / 测试 / 无货币）：仍是现行码，但 minor units 无定义，
 * 不是可定价币种（字典 65 §9）。
 */
export const ISO_4217_NO_MINOR_UNIT_CODES: readonly string[] = Object.freeze(
  'XAG XAU XBA XBB XBC XBD XDR XPD XPT XSU XTS XUA XXX'.split(' '),
);

const CURRENT_CURRENCY_CODE_SET: ReadonlySet<string> = new Set(ISO_4217_CURRENT_CURRENCY_CODES);
const NO_MINOR_UNIT_CODE_SET: ReadonlySet<string> = new Set(ISO_4217_NO_MINOR_UNIT_CODES);

/** 是否 ISO 4217 现行币种码（区分大小写：小写、两位、四位与已退出 List One 的码均为 false）。 */
export function isIso4217CurrentCurrencyCode(code: string): boolean {
  return CURRENT_CURRENCY_CODE_SET.has(code);
}

/**
 * 是否可定价币种（字典 65 §9）：ISO 4217 现行码且小数位有定义。非现行码、小写与 N.A. 码（`XAU` / `XTS` / `XXX` 等）
 * 均为 false —— 写侧据此拒收，选价据此判选不出，不按 2 位或其他小数位猜测换算。
 */
export function isPriceableCurrency(code: string): boolean {
  return CURRENT_CURRENCY_CODE_SET.has(code) && !NO_MINOR_UNIT_CODE_SET.has(code);
}
